import { useContext } from 'react';
import { AuthContext } from '../AuthContext';
import authService from '../services/authService';

/**
 * Custom hook for register functionality
 * Handles registration API call and updates auth context state if auto-login occurs
 * @returns {Object} { register, isLoading, error }
 */
export const useRegister = () => {
  const { setUser, setSelectedRole, setRoles, setLoading, loading } = useContext(AuthContext);

  const register = async (email, password, confirmPassword, firstName, lastName, department, institute_id, mobileNumber = null, recaptchaToken = null) => {
    try {
      setLoading(true);

      if (password !== confirmPassword) {
        return { success: false, error: "Passwords do not match." };
      }

      const result = await authService.register(email, password, confirmPassword, firstName, lastName, department, institute_id, mobileNumber, recaptchaToken);

      if (result.success) {
        // Check if email verification is required
        const requiresVerification = result.data.email_verified === false || result.data.verification_email_sent === true;
        
        // Only auto-login if tokens are provided AND email is verified
        if (result.data.access && result.data.refresh && !requiresVerification) {
          authService.storeAuthData(result.data);

          const userInfo = result.data.user || { email, name: email.split('@')[0] };
          setUser(userInfo);

          // Set roles in context if available
          if (result.data.roles && Array.isArray(result.data.roles)) {
            setRoles(result.data.roles);
            // Set default role (lowest privilege level)
            const defaultRole = authService.getDefaultRole(result.data.roles);
            if (defaultRole) {
              authService.storeSelectedRole(defaultRole);
              setSelectedRole(defaultRole);
            }
          } else if (result.data.user?.roles && Array.isArray(result.data.user.roles)) {
            setRoles(result.data.user.roles);
            const defaultRole = authService.getDefaultRole(result.data.user.roles);
            if (defaultRole) {
              authService.storeSelectedRole(defaultRole);
              setSelectedRole(defaultRole);
            }
          }
        }

        // Return full data including verification status
        return { 
          success: true, 
          message: result.data.message || "Registration successful!",
          data: result.data
        };
      } else {
        return { success: false, error: result.error };
      }
    } catch (error) {
      console.error("Registration error:", error);
      return { success: false, error: "An unexpected error occurred." };
    } finally {
      setLoading(false);
    }
  };

  return { register, isLoading: loading };
};
